import type { ReactNode } from 'react';
import type { ListingDetail } from '../../dto/listing.js';
import type { LotMemberForBuyer } from '../../dto/marketplace.js';
import { genericArtFor, listingImageModel } from '../../listing-image';
import { Card } from '../Card';
import { ListingImage } from '../ListingImage';
import { PageHeader } from '../PageHeader';
import { ListingStatusBadge } from '../ListingStatusBadge';
import { formatDate } from '../format';
import { ActionCard, type ActionCardProps } from './ActionCard';
import { CommercialTermsTiles, TechnicalDetailsTiles } from './DetailTiles';
import { LotContentsTable } from './LotContentsTable';
import { LotUnitPricesCard } from './LotUnitPricesCard';
import { PassportCard } from './PassportCard';
import { evidenceOf, nodeCountOf, sellerVerifiedOf, technicalDetailsOf } from './listingShape';
import {
  LISTING_ACCEPTING_OFFERS,
  LISTING_NOT_ACCEPTING_OFFERS,
  listedOnLabel,
  listingIdEyebrow,
} from './copy/listingDetail';
import { LISTING_CARD_VERIFIED_SELLER } from './copy/marketplace';

/**
 * SIL-1541: the ONE buyer-facing listing presentation, shared by the customer
 * portal listing page (`apps/customer/src/routes/listings_.$listingId.tsx`)
 * and the ops "View as buyer" preview (`apps/operations/.../arbitrage-listings_.$listingId.tsx`).
 *
 * Input is the buyer-shaped `ListingDetail` payload and nothing else - there is
 * no floor, source or FMV-internals field on that type to render. Everything a
 * host app owns (routing, auth-bound fetches, dialogs, the manufacturer logo
 * asset pipeline) enters through the slot props, so this module imports no
 * router, no API client and no app-local component. A host that needs a
 * different structure adds a slot, it does not fork the markup.
 */
export interface BuyerListingViewProps {
  listing: ListingDetail;
  /** Rendered above the header card (customer: the "back to marketplace" link). */
  backLink?: ReactNode;
  /** The manufacturer mark, rendered in the header chip row. */
  renderManufacturerLogo?: (listing: ListingDetail) => ReactNode;
  /** Lot table: open a node Passport drawer. Absent = fall back to `renderLotPassportLink`. */
  onOpenNodePassport?: (member: LotMemberForBuyer) => void;
  /** Lot table: the Passport cell when no drawer is wired (route link). */
  renderLotPassportLink?: (member: LotMemberForBuyer) => ReactNode;
  /** Single-node digital passport summary, shown for verified-tier non-lot listings. */
  renderPassportSummary?: (listing: ListingDetail) => ReactNode;
  /** Avoided-carbon claim, below the commercial terms tiles. */
  carbonClaim?: ReactNode;
  /** Slots that follow the carbon claim in the left column (lot environmental, bids, offers...). */
  leftColumnExtras?: ReactNode;
  /** The right-hand action card's state and handlers (`onGetVerified` also drives the Passport card). */
  action: Pick<
    ActionCardProps,
    'canTransact' | 'buyerVerified' | 'onBuyNow' | 'onMakeOffer' | 'onGetVerified'
  >;
}

const CHIP_CLASS =
  'rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground ring-1 ring-inset ring-border';

export function BuyerListingView({
  listing,
  backLink,
  renderManufacturerLogo,
  onOpenNodePassport,
  renderLotPassportLink,
  renderPassportSummary,
  carbonClaim,
  leftColumnExtras,
  action,
}: BuyerListingViewProps) {
  // SIL-1377 - the three skew-prone blocks, read once through `listingShape`.
  // `evidence` being `undefined` means the evidence-derived sections below are
  // OMITTED, never filled with a stand-in: an absent block is the neutral/unknown
  // state and must not be able to produce a Verified mark or an exact price.
  const evidence = evidenceOf(listing);
  const technical = technicalDetailsOf(listing);
  const nodeCount = nodeCountOf(listing);
  const gpusPerNode = technical.gpusPerNode ?? null;
  const gpuCount = gpusPerNode === null ? null : gpusPerNode * nodeCount;

  const title =
    listing.displayTitle ??
    listing.skuModelName ??
    listing.skuShortDescription ??
    listing.id.slice(0, 8);

  // SIL-1550: product photo (SKU/family) or generic form-factor art, the same
  // `ListingImage` slot the public exchange listing page uses.
  const imageModel = listingImageModel({
    image: listing.image ?? null,
    alt: title,
    art: genericArtFor({
      assetClassId: listing.assetClassId,
      names: [listing.skuModelName, listing.skuShortDescription, title],
      hasGpus: technical.gpuModel !== undefined || gpusPerNode !== null,
      isServer: technical.serverModel !== undefined || gpusPerNode !== null,
    }),
  });

  const badges = [
    listing.manufacturerName,
    technical.architecture,
    listing.listingType,
    listing.conditionBand,
  ].filter((b): b is string => typeof b === 'string' && b.length > 0);

  return (
    <div className="space-y-6">
      {backLink}

      <Card as="section" className="flex flex-col gap-5 sm:flex-row sm:items-start">
        <ListingImage model={imageModel} variant="detail" className="w-full sm:w-72 sm:shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-mono text-xs text-muted-foreground">{listingIdEyebrow(listing.id)}</p>
          <PageHeader
            className="mt-1"
            title={title}
            subtitle={listing.assetClassName ?? `${listing.listingType} listing`}
            actions={
              <div className="flex flex-col items-end gap-1">
                <div className="flex items-center gap-2">
                  <ListingStatusBadge value={listing.status} availableOn={listing.availableOn} />
                  <span
                    className={
                      listing.acceptingOffers
                        ? 'rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200'
                        : CHIP_CLASS
                    }
                  >
                    {listing.acceptingOffers
                      ? LISTING_ACCEPTING_OFFERS
                      : LISTING_NOT_ACCEPTING_OFFERS}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {listedOnLabel(formatDate(listing.listedAt))}
                </span>
              </div>
            }
          />
          {(badges.length > 0 || sellerVerifiedOf(listing)) && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {listing.manufacturerName && renderManufacturerLogo?.(listing)}
              {/* SIL-1480: positive-only - renders only when true. */}
              {sellerVerifiedOf(listing) && (
                <span data-testid="verified-seller-marker" className={CHIP_CLASS}>
                  {LISTING_CARD_VERIFIED_SELLER}
                </span>
              )}
              {badges
                .filter((b) => b !== listing.manufacturerName)
                .map((b) => (
                  <span key={b} className={CHIP_CLASS}>
                    {b}
                  </span>
                ))}
            </div>
          )}
        </div>
      </Card>

      <div className="flex flex-wrap gap-6">
        {/* Left column - spec 4.2: flex 1 1 560px. */}
        <div className="min-w-0 flex-[1_1_560px] space-y-6">
          {/* SIL-1377 - the Passport card IS the evidence surface: its chip, its
              pass ratio and its custody body are all read off the block. With no
              block there is nothing it could honestly draw, so it is omitted. */}
          {evidence !== undefined && (
            <PassportCard
              evidence={evidence}
              pricing={listing.pricing}
              passportId={listing.assetFingerprint}
              nodeCount={nodeCount}
              onGetVerified={action.onGetVerified}
            />
          )}

          {listing.listingType === 'lot' && (
            <LotContentsTable
              members={listing.members ?? []}
              lotMembers={listing.lotMembers}
              nodeCount={nodeCount}
              gpuCount={gpuCount}
              onOpenNodePassport={onOpenNodePassport}
              renderPassportLink={renderLotPassportLink}
            />
          )}

          {listing.listingType === 'lot' && listing.unitPrices != null && listing.unitPrices.length > 0 && (
            <LotUnitPricesCard unitPrices={listing.unitPrices} />
          )}

          {listing.listingType !== 'lot' &&
            listing.assetFingerprint !== null &&
            evidence?.tier === 'verified' &&
            renderPassportSummary?.(listing)}

          <TechnicalDetailsTiles details={technical} />

          <CommercialTermsTiles
            pricing={listing.pricing}
            evidence={evidence}
            listingType={listing.listingType}
            shipsWithinDays={listing.shipsWithinDays}
            paymentTerms={listing.paymentTerms}
          />

          {carbonClaim}

          {leftColumnExtras}
        </div>

        {/* Right column - spec 4.2: flex 1 1 300px, max 380px, sticky. An
            auction listing has no list price to buy at, so the bidding panel
            (a host slot in the left column) is its action surface and this card
            is omitted rather than shown with dead buttons. */}
        {listing.saleMechanic !== 'auction' && (
          <div className="flex-[1_1_300px] self-start sm:sticky sm:top-6 sm:max-w-[380px]">
            <ActionCard
              pricing={listing.pricing}
              nodeCount={nodeCount}
              purchasePending={listing.purchasePending}
              offersOnly={listing.offersOnly}
              {...action}
            />
          </div>
        )}
      </div>
    </div>
  );
}
